import React from 'react';
import { Target, AlertCircle } from 'lucide-react';

const CorrelationMatrix = ({ correlations }) => {
  const getSignificanceColor = (significance) => {
    if (significance >= 0.7) return 'bg-red-500';
    if (significance >= 0.4) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const symptomCorrelations = correlations?.filter(c => c?.type === 'symptom_pair') || [];
  const triggerCorrelations = correlations?.filter(c => c?.type === 'trigger') || [];

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 bg-blue-100 rounded-lg">
          <Target className="w-6 h-6 text-blue-600" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900">Correlation Analysis</h2>
          <p className="text-sm text-gray-600">Discover relationships between symptoms and triggers</p>
        </div>
      </div>

      {/* Symptom Co-occurrence */}
      {symptomCorrelations?.length > 0 && (
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Symptom Co-occurrence</h3>
          <div className="space-y-3">
            {symptomCorrelations
              ?.sort((a, b) => b?.significance - a?.significance)
              ?.slice(0, 10)
              ?.map((correlation, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{correlation?.items}</div>
                      <div className="text-sm text-gray-600 mt-1">
                        Occurred together {correlation?.frequency} times
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <div className="text-sm font-medium text-gray-700">
                          {(correlation?.significance * 100)?.toFixed(0)}% of entries
                        </div>
                        <div className="text-xs text-gray-500">
                          {(correlation?.confidence * 100)?.toFixed(0)}% confidence
                        </div>
                      </div>
                      <div
                        className={`w-3 h-3 rounded-full ${getSignificanceColor(correlation?.significance)}`}
                        title="Significance indicator"
                      />
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-3">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all"
                      style={{ width: `${correlation?.significance * 100}%` }}
                    />
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Trigger Correlations */}
      {triggerCorrelations?.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Trigger Impact Analysis</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {triggerCorrelations
              ?.sort((a, b) => b?.avgSeverity - a?.avgSeverity)
              ?.map((correlation, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-semibold text-gray-900 capitalize">{correlation?.trigger}</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        Triggered {correlation?.frequency} times
                      </p>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      correlation?.avgSeverity >= 7
                        ? 'bg-red-100 text-red-700'
                        : correlation?.avgSeverity >= 4
                        ? 'bg-yellow-100 text-yellow-700' :'bg-green-100 text-green-700'
                    }`}>
                      Avg: {correlation?.avgSeverity?.toFixed(1)}/10
                    </span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Impact Score</span>
                      <span className="font-medium text-gray-900">
                        {(correlation?.significance * 10)?.toFixed(1)}/10
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${
                          correlation?.avgSeverity >= 7
                            ? 'bg-red-500'
                            : correlation?.avgSeverity >= 4
                            ? 'bg-yellow-500' :'bg-green-500'
                        }`}
                        style={{ width: `${(correlation?.avgSeverity / 10) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {(!correlations || correlations?.length === 0) && (
        <div className="text-center py-8">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">No significant correlations detected yet</p>
          <p className="text-sm text-gray-500 mt-1">More data needed to identify relationships</p>
        </div>
      )}
    </div>
  );
};

export default CorrelationMatrix;